/**
 * The workspace: columns of panels, dragged and resized by the trader.
 *
 * ## This file owns arrangement and nothing else
 *
 * It takes a `render` function and calls it per panel kind. It never knows
 * what a chart is, never holds market data, and never fetches. `App` owns the
 * data and supplies the renderer; this owns where things sit. Without that
 * split, adding a panel would mean threading its props through the layout.
 *
 * Every mutation is a pure function from `@aegis/core` — `movePane`,
 * `resizeColumn`, `resizePane`, `removePane`, `detachPane`. There is no
 * layout arithmetic here at all, which is what makes the layout testable when
 * a React component is not.
 *
 * ## Dragging is not the only way to move a panel
 *
 * Every drag has a keyboard equivalent in the panel's own menu — move up,
 * down, left, right. A layout editor that can only be operated by pointer is
 * one a trader using a keyboard cannot arrange at all, and the operations
 * already exist as functions; exposing them as buttons costs a menu.
 *
 * Dividers are focusable and respond to arrow keys for the same reason.
 */
import { useCallback, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { Pane, PanelKind, Workspace } from '@aegis/core';
import {
  columnTemplate, configurePane, detachPane, gridTemplate, movePane, paneKind,
  panelSpec, removePane,
  resizeColumn, resizePane,
} from '@aegis/core';
import { Icon } from './Icon.js';

/** Divider thickness in pixels. Also its grid track, so the two cannot drift. */
const DIVIDER_PX = 6;

export function WorkspaceGrid({
  workspace, onEdit, onDetach, canDetach, render, symbols, defaultSymbol,
}: {
  workspace: Workspace;
  onEdit: (fn: (ws: Workspace) => Workspace) => void;
  /** Called after the layout records the detach, so the shell can open a window. */
  onDetach: (paneId: string) => void;
  /** False in a browser tab, where there is no such thing as a second window. */
  canDetach: boolean;
  /**
   * The pane, not just its kind.
   *
   * A chart instance needs its own id to join the crosshair bus and its own
   * config to know which instrument it is on. Passing only the kind would make
   * every instance of a kind render identically, which is the opposite of what
   * instances are for.
   */
  render: (pane: Pane) => ReactNode;
  /** Roots a pane may be pointed at, for the per-pane picker. */
  symbols: readonly string[];
  /** What a pane showing no symbol of its own is following. */
  defaultSymbol: string;
}): JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ column: number; index: number } | null>(null);

  const beginDrag = useCallback((paneId: string, e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    setDragging(paneId);

    const onMove = (ev: PointerEvent): void => setDropAt(dropTarget(ev.clientX, ev.clientY));
    const onUp = (ev: PointerEvent): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const target = dropTarget(ev.clientX, ev.clientY);
      setDragging(null);
      setDropAt(null);
      if (target) onEdit((ws) => movePane(ws, paneId, target.column, target.index));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [onEdit]);

  /** Column dividers drag horizontally; pane dividers drag vertically. */
  const beginResize = useCallback((
    e: React.PointerEvent, axis: 'x' | 'y', columnIndex: number, paneIndex?: number,
  ) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);

    const onMove = (ev: PointerEvent): void => {
      const grid = gridRef.current;
      if (!grid) return;
      const at = axis === 'x'
        ? fraction(grid, columnIndex, ev.clientX, 'x')
        : fraction(grid, columnIndex, ev.clientY, 'y', paneIndex);
      if (at === null) return;
      onEdit((ws) => (axis === 'x'
        ? resizeColumn(ws, columnIndex, pairWeight(ws, columnIndex) * at)
        : resizePane(
          ws, columnIndex, paneIndex ?? 0,
          panePairWeight(ws, columnIndex, paneIndex ?? 0) * at,
        )));
    };
    const onUp = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [onEdit]);

  return (
    <div
      ref={gridRef}
      className={`ws-grid ${dragging ? 'ws-dragging' : ''}`}
      style={{ gridTemplateColumns: gridTemplate(workspace, DIVIDER_PX) }}
    >
      {workspace.columns.map((column, ci) => [
        <div
          key={`c${ci}`}
          className="ws-col"
          data-column={ci}
          style={{ gridTemplateRows: columnTemplate(column, DIVIDER_PX) }}
        >
          {column.panes.map((pane, pi) => [
            <section
              key={pane.kind}
              className={`ws-pane ${dragging === pane.id ? 'ws-pane-lifted' : ''}`}
              data-pane={pi}
              aria-label={panelSpec(pane.kind)?.label ?? pane.kind}
            >
              {/* Two halves so a drop lands above or below what the pointer is
                  over, which is what the pointer position means. */}
              {dropAt?.column === ci && dropAt.index === pi && (
                <span className="ws-drop-line ws-drop-before" aria-hidden="true" />
              )}
              {dropAt?.column === ci && dropAt.index === pi + 1 && (
                <span className="ws-drop-line ws-drop-after" aria-hidden="true" />
              )}

              <header className="ws-pane-head">
                <button
                  type="button"
                  className="ws-grip"
                  onPointerDown={(e) => beginDrag(pane.id, e)}
                  aria-label={`Move ${panelSpec(pane.kind)?.label ?? pane.kind}`}
                >
                  <Icon name="drag" size="sm" />
                </button>
                <h3 className="ws-pane-title">
                  {panelSpec(pane.kind)?.label ?? pane.kind}
                </h3>
                {panelSpec(pane.kind)?.symbolScoped && (
                  <PaneSymbol
                    pane={pane}
                    symbols={symbols}
                    defaultSymbol={defaultSymbol}
                    onEdit={onEdit}
                  />
                )}
                <PaneMenu
                  paneId={pane.id}
                  column={ci}
                  index={pi}
                  workspace={workspace}
                  canDetach={canDetach}
                  onEdit={onEdit}
                  onDetach={onDetach}
                />
              </header>
              <div className="ws-pane-body">{render(pane)}</div>
            </section>,

            pi < column.panes.length - 1 && (
              <div
                key={`d${pi}`}
                className="ws-divider ws-divider-y"
                role="separator"
                aria-orientation="horizontal"
                aria-label={`Resize ${panelSpec(pane.kind)?.label ?? pane.kind}`}
                tabIndex={0}
                onPointerDown={(e) => beginResize(e, 'y', ci, pi)}
                onKeyDown={(e) => nudge(e, (delta) => onEdit((ws) => resizePane(
                  ws, ci, pi, (ws.columns[ci]?.panes[pi]?.weight ?? 1) + delta,
                )), 'y')}
              />
            ),
          ])}
        </div>,

        ci < workspace.columns.length - 1 && (
          <div
            key={`cd${ci}`}
            className="ws-divider ws-divider-x"
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize column ${ci + 1}`}
            tabIndex={0}
            onPointerDown={(e) => beginResize(e, 'x', ci)}
            onKeyDown={(e) => nudge(e, (delta) => onEdit((ws) => resizeColumn(
              ws, ci, (ws.columns[ci]?.weight ?? 1) + delta,
            )), 'x')}
          />
        ),
      ])}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * What one panel is looking at.
 *
 * `Follow` is the default and is a real value, not an absence dressed up: a
 * panel following the application moves when the trader changes symbol, and a
 * panel pointed at CL does not. Storing "CL" for a panel the trader never
 * touched would silently pin it the first time they looked at CL.
 *
 * Only on panels the registry marks `symbolScoped` — a rule tracker is about
 * an account and has no instrument to choose.
 */
function PaneSymbol({
  pane, symbols, defaultSymbol, onEdit,
}: {
  pane: Pane;
  symbols: readonly string[];
  defaultSymbol: string;
  onEdit: (fn: (ws: Workspace) => Workspace) => void;
}): JSX.Element {
  const chosen = pane.config?.symbol ?? '';
  return (
    <label className="ws-pane-symbol">
      <span className="sr-only">
        {`Instrument for ${panelSpec(pane.kind)?.label ?? pane.kind}`}
      </span>
      <select
        className="select compact"
        value={chosen}
        onChange={(e) => onEdit((ws) => configurePane(ws, pane.id, {
          // Empty string clears it back to following. `configurePane` merges,
          // so an empty value has to be passed explicitly rather than omitted.
          symbol: e.target.value,
        }))}
      >
        <option value="">Follow · {defaultSymbol}</option>
        {symbols.map((root) => <option key={root} value={root}>{root}</option>)}
      </select>
    </label>
  );
}

/**
 * The keyboard path, and the only way to detach.
 *
 * Not a duplicate of the drag: it is the same operations reached differently,
 * and it is what makes the layout arrangeable without a pointer at all.
 */
function PaneMenu({
  paneId, column, index, workspace, canDetach, onEdit, onDetach,
}: {
  paneId: string;
  column: number;
  index: number;
  workspace: Workspace;
  canDetach: boolean;
  onEdit: (fn: (ws: Workspace) => Workspace) => void;
  onDetach: (paneId: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const panes = workspace.columns[column]?.panes.length ?? 0;
  const kind = paneKind(paneId);
  const label = (kind && panelSpec(kind)?.label) ?? paneId;

  const act = (fn: (ws: Workspace) => Workspace): void => { onEdit(fn); setOpen(false); };

  return (
    <div className="ws-pane-menu">
      <button
        type="button"
        className="ws-pane-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${label} options`}
      >
        <Icon name="more" size="sm" />
      </button>

      {open && (
        <div className="ws-menu" role="menu">
          <button
            type="button" role="menuitem"
            disabled={index === 0}
            onClick={() => act((ws) => movePane(ws, paneId, column, index - 1))}
          >
            Move up
          </button>
          <button
            type="button" role="menuitem"
            disabled={index >= panes - 1}
            onClick={() => act((ws) => movePane(ws, paneId, column, index + 2))}
          >
            Move down
          </button>
          <button
            type="button" role="menuitem"
            disabled={column === 0}
            onClick={() => act((ws) => movePane(ws, paneId, column - 1, 0))}
          >
            Move left
          </button>
          <button
            type="button" role="menuitem"
            disabled={column >= workspace.columns.length - 1}
            onClick={() => act((ws) => movePane(ws, paneId, column + 1, 0))}
          >
            Move right
          </button>

          {canDetach && (
            <button
              type="button" role="menuitem"
              onClick={() => {
                // The layout records the detach and the shell opens the
                // window. In that order: a workspace claiming a panel is
                // detached into a window that never opened leaves it in
                // neither place.
                onEdit((ws) => detachPane(ws, paneId));
                onDetach(paneId);
                setOpen(false);
              }}
            >
              Open in its own window
            </button>
          )}

          <button
            type="button" role="menuitem" className="ws-menu-remove"
            onClick={() => act((ws) => removePane(ws, paneId))}
          >
            Remove from layout
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Where a pointer is, as a column and an insertion index.
 *
 * Read off the DOM rather than from a registry of rectangles kept in state. A
 * registry has to be refreshed on every resize, every scroll and every layout
 * change, and when it is stale a drop lands in the wrong column with nothing
 * on screen to suggest why.
 *
 * The half of the pane the pointer is in decides before or after, which is
 * what a pointer position means to the person holding it.
 */
function dropTarget(x: number, y: number): { column: number; index: number } | null {
  const el = document.elementFromPoint(x, y);
  const columnEl = el?.closest('[data-column]');
  if (!(columnEl instanceof HTMLElement)) return null;
  const column = Number(columnEl.dataset.column);
  if (!Number.isInteger(column)) return null;

  const paneEl = el?.closest('[data-pane]');
  if (!(paneEl instanceof HTMLElement)) {
    // Over the column but not over a pane — the gap under the last one.
    return { column, index: columnEl.querySelectorAll('[data-pane]').length };
  }

  const index = Number(paneEl.dataset.pane);
  const box = paneEl.getBoundingClientRect();
  return { column, index: y > box.top + box.height / 2 ? index + 1 : index };
}

/**
 * How far along the resizable pair the pointer sits, 0 to 1.
 *
 * Measured across the two tracks the divider is between, not across the whole
 * grid: `resizeColumn` and `resizePane` both take a weight on the PAIR's
 * scale, and measuring across everything would make a drag near one edge jump.
 */
function fraction(
  grid: HTMLElement, columnIndex: number, position: number,
  axis: 'x' | 'y', paneIndex?: number,
): number | null {
  const columns = grid.querySelectorAll<HTMLElement>('[data-column]');

  if (axis === 'x') {
    const a = columns[columnIndex];
    const b = columns[columnIndex + 1];
    if (!a || !b) return null;
    const from = a.getBoundingClientRect().left;
    const to = b.getBoundingClientRect().right;
    return to > from ? clamp01((position - from) / (to - from)) : null;
  }

  const column = columns[columnIndex];
  if (!column || paneIndex === undefined) return null;
  const panes = column.querySelectorAll<HTMLElement>('[data-pane]');
  const a = panes[paneIndex];
  const b = panes[paneIndex + 1];
  if (!a || !b) return null;
  const from = a.getBoundingClientRect().top;
  const to = b.getBoundingClientRect().bottom;
  return to > from ? clamp01((position - from) / (to - from)) : null;
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function pairWeight(ws: Workspace, index: number): number {
  return (ws.columns[index]?.weight ?? 1) + (ws.columns[index + 1]?.weight ?? 1);
}

function panePairWeight(ws: Workspace, column: number, pane: number): number {
  const panes = ws.columns[column]?.panes ?? [];
  return (panes[pane]?.weight ?? 1) + (panes[pane + 1]?.weight ?? 1);
}

/** Arrow keys on a focused divider. A tenth of a share per press. */
function nudge(
  e: React.KeyboardEvent, apply: (delta: number) => void, axis: 'x' | 'y',
): void {
  const less = axis === 'x' ? 'ArrowLeft' : 'ArrowUp';
  const more = axis === 'x' ? 'ArrowRight' : 'ArrowDown';
  if (e.key !== less && e.key !== more) return;
  e.preventDefault();
  apply(e.key === more ? 0.1 : -0.1);
}
